/**
 * Contract for offline PWA sync with ADaMS API (frontend–backend alignment).
 * Backend colleague: extend Auth login/refresh, POST final with Idempotency-Key, import.
 */

import { NewApiBaseUrl } from '../consts/new-api-base-url'

/** Payload and export schema version — bump when breaking changes occur. */
export const INCIDENT_SCHEMA_VERSION = '1.0.0'

/** Enable mock responses for final/refresh in development (no real API). */
export const USE_OFFLINE_API_MOCK =
  typeof process !== 'undefined' && process.env.REACT_APP_MOCK_OFFLINE_API === 'true'

/**
 * @typedef {Object} LoginResponseExtended
 * @property {string} token - JWT access token
 * @property {string} [refreshToken] - Opaque refresh token (approach A)
 * @property {string} [last_login_date]
 */

/**
 * @typedef {Object} RefreshResponse
 * @property {string} token
 * @property {string} [refreshToken]
 */

/**
 * @typedef {Object} FinalIncidentResponse
 * @property {string|number} crash_id - Server final crash id
 * @property {boolean} [duplicate] - True if idempotency replay
 */

export function authLoginUrl() {
  return `${NewApiBaseUrl}/Auth/Login`
}

export function authInitUrl() {
  return `${NewApiBaseUrl}/Auth/Init`
}

export function crashValidatedSummaryUrl() {
  return `${NewApiBaseUrl}/Crash/validated-summary`
}

export function authRefreshUrl() {
  const override = process.env.REACT_APP_AUTH_REFRESH_PATH
  if (override) return `${NewApiBaseUrl}/${override.replace(/^\//, '')}`
  return `${NewApiBaseUrl}/Auth/Refresh`
}

/**
 * POST body for refresh (adjust field names when backend contract is fixed).
 */
export function buildRefreshBody(refreshToken) {
  return { refreshToken }
}

/**
 * Idempotent submit of a final incident (offline queue → server).
 * Path may be Crash/Final or Incidents/Final — align with backend OpenAPI.
 */
export function incidentFinalUrl() {
  return `${NewApiBaseUrl}/Crash/final`
}

export const IDEMPOTENCY_HEADER = 'Idempotency-Key'

/**
 * Normalizza i principali codici errore backend in categorie utili per retry policy.
 * @param {number|undefined} status
 * @returns {'auth'|'forbidden'|'conflict'|'rate_limited'|'server'|'client'|'unknown'}
 */
export function classifyOfflineApiError(status) {
  if (status === 401) return 'auth'
  if (status === 403) return 'forbidden'
  if (status === 409) return 'conflict'
  if (status === 429) return 'rate_limited'
  if (typeof status === 'number' && status >= 500) return 'server'
  if (typeof status === 'number' && status >= 400) return 'client'
  return 'unknown'
}

/**
 * Builds export envelope for manual file transfer (chiavetta / central import).
 * @param {Object} params
 * @param {string} params.clientUuid
 * @param {unknown} params.payload - Full incident payload (same as API body)
 * @returns {Object}
 */
export function buildExportEnvelope({ clientUuid, payload }) {
  return {
    schemaVersion: INCIDENT_SCHEMA_VERSION,
    clientUuid,
    exportedAt: new Date().toISOString(),
    payload
  }
}
